import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams, useRouteError } from 'react-router-dom';
import './admin.css';
import { reportError } from './api';
import { AdminProvider, useAdmin } from './lib';
import { Loading, Shell } from './ui';
import { Login } from './pages/login';
import { Overview } from './pages/overview';
import { Customers } from './pages/customers';
import { Codes, Orders, Products } from './pages/sales';
import { Documents, Generator, TrafficPage } from './pages/activity';
import { Audit, Errors, Notices, Settings } from './pages/system';

window.addEventListener('error', (e) => reportError(e.message, e.error?.stack));
window.addEventListener('unhandledrejection', (e) => reportError(String(e.reason?.message ?? e.reason), e.reason?.stack));

function RouteError() {
  const e = useRouteError() as Error;
  reportError(e?.message ?? 'route error', e?.stack);
  return <div className="wrap" style={{ padding: '3rem var(--gutter)' }}><h1>Something went wrong</h1><p>{e?.message}</p><button className="btn" onClick={() => location.reload()}>Reload</button></div>;
}

/** An old address, or a section's own address: go to the page, keeping the id and the query string. */
function To({ path }: { path: string }) {
  const { id } = useParams();
  const loc = useLocation();
  return <Navigate to={`${path.replace(':id', id ?? '')}${loc.search}`} replace />;
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Overview /> },
      { path: '/customers', element: <Customers /> },
      { path: '/customers/:id', element: <Customers /> },
      { path: '/sales', element: <To path="/sales/orders" /> },
      { path: '/sales/orders', element: <Orders /> },
      { path: '/sales/orders/:id', element: <Orders /> },
      { path: '/sales/codes', element: <Codes /> },
      { path: '/sales/products', element: <Products /> },
      { path: '/activity', element: <To path="/activity/traffic" /> },
      { path: '/activity/traffic', element: <TrafficPage /> },
      { path: '/activity/documents', element: <Documents /> },
      { path: '/activity/generator', element: <Generator /> },
      { path: '/system', element: <To path="/system/errors" /> },
      { path: '/system/errors', element: <Errors /> },
      { path: '/system/audit', element: <Audit /> },
      { path: '/system/notices', element: <Notices /> },
      { path: '/system/settings', element: <Settings /> },
      // the old one-page-per-table addresses
      { path: '/accounts', element: <To path="/customers" /> },
      { path: '/accounts/:id', element: <To path="/customers/:id" /> },
      { path: '/orders', element: <To path="/sales/orders" /> },
      { path: '/orders/:id', element: <To path="/sales/orders/:id" /> },
      { path: '/codes', element: <To path="/sales/codes" /> },
      { path: '/traffic', element: <To path="/activity/traffic" /> },
      { path: '/documents', element: <To path="/activity/documents" /> },
      { path: '/generator', element: <To path="/activity/generator" /> },
      { path: '/errors', element: <To path="/system/errors" /> },
      { path: '/audit', element: <To path="/system/audit" /> },
      { path: '/notices', element: <To path="/system/notices" /> },
      { path: '/settings', element: <To path="/system/settings" /> },
      { path: '*', element: <div className="page"><h1>Page not found</h1></div> },
    ],
  },
]);

/** Everything sits behind a full (password + authenticator) admin session. */
function Gate() {
  const { admin, loading } = useAdmin();
  if (loading) return <Loading />;
  if (!admin || admin.stage !== 'full') return <Login />;
  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AdminProvider>
      <Gate />
    </AdminProvider>
  </StrictMode>,
);
